import React from "react";

interface TabsContextType {
  activeTab: string;
  setActiveTab: (value: string) => void;
}

const TabsContext = React.createContext<TabsContextType | null>(null);

interface TabsProps {
  children: React.ReactNode;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
}

export function Tabs({
  children,
  defaultValue = "",
  onValueChange,
}: TabsProps) {
  const [activeTab, setActiveTab] = React.useState(defaultValue);

  React.useEffect(() => {
    if (onValueChange) {
      onValueChange(activeTab);
    }
  }, [activeTab, onValueChange]);

  const value = React.useMemo(() => ({ activeTab, setActiveTab }), [activeTab]);

  return (
    <TabsContext.Provider value={value}>
      <div className="w-full">{children}</div>
    </TabsContext.Provider>
  );
}

interface TabsListProps {
  children: React.ReactNode;
  className?: string;
}

export function TabsList({ children, className = "" }: TabsListProps) {
  // Convert children to array to handle optional dividers
  const childrenArray = React.Children.toArray(children);

  return (
    <div className={`flex w-full justify-start ${className}`}>
      {childrenArray.map((child, index) => (
        <React.Fragment key={index}>
          {child}
          {index < childrenArray.length - 1 && (
            <div className="w-px bg-gray-300 my-2" />
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

interface TabsTriggerProps {
  value: string;
  children: React.ReactNode;
  className?: string;
}

export function TabsTrigger({
  value,
  children,
  className = "",
}: TabsTriggerProps) {
  const ctx = React.useContext(TabsContext);

  if (!ctx) {
    throw new Error("TabsTrigger must be used inside a <Tabs> component.");
  }

  const { activeTab, setActiveTab } = ctx;
  const isActive = activeTab === value;

  return (
    <button
      className={`
        px-4 py-3 text-[0.875rem] font-medium transition-colors hover:text-[var(--color-primary-light)]
        ${
          isActive
            ? "text-[var(--color-primary)] border-b-2 border-[var(--color-primary)]"
            : "text-gray-600"
        }
        ${className}
      `}
      onClick={() => setActiveTab(value)}
    >
      {children}
    </button>
  );
}

interface TabsContentProps {
  value: string;
  children: React.ReactNode;
  className?: string;
}

export function TabsContent({
  value,
  children,
  className = "",
}: TabsContentProps) {
  const ctx = React.useContext(TabsContext);

  if (!ctx) {
    throw new Error("TabsContent must be used inside a <Tabs> component.");
  }

  const { activeTab } = ctx;

  if (activeTab !== value) return null;

  return <div className={`bg-white ${className}`}>{children}</div>;
}
